import React, { useState, useEffect } from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import { useNavigate } from 'react-router-dom';
import buddyService from '../services/buddyService';
import { toast, ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserFriends, 
  faArrowLeft,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faChartBar,
  faUsers,
  faEnvelope,
  faMessage,
  faPhone,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import 'react-toastify/dist/ReactToastify.css';

const FacilitatorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending'); // pending, active, stats
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeMatches, setActiveMatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 768;
  const isSmall = windowWidth <= 480;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load pending requests
      const requestsResponse = await buddyService.getFacilitatorRequests('pending');
      setPendingRequests(requestsResponse.requests || []);

      // Load active matches
      const matchesResponse = await buddyService.getFacilitatorMatches();
      setActiveMatches(matchesResponse.matches || []);

      // Load stats
      const statsResponse = await buddyService.getFacilitatorStats();
      setStats(statsResponse);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 403) {
        toast.error('You are not a facilitator');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load facilitator data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId, message = '') => {
    setActionLoading(true);
    try {
      await buddyService.acceptRequest(requestId, message);
      toast.success('Request accepted! Match created.');
      setSelectedRequest(null);
      setResponseMessage('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to accept request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectRequest = async (requestId, message = '') => {
    setActionLoading(true);
    try {
      await buddyService.rejectRequest(requestId, message);
      toast.success('Request rejected');
      setSelectedRequest(null);
      setResponseMessage('');
      loadData();
    } catch (error) {
      toast.error('Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            <FontAwesomeIcon icon={faUserFriends} style={{ color: '#FFB6C1' }} spin />
          </div>
          <p>Loading facilitator dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      padding: isMobile ? '1rem' : '2rem'
    }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <button 
          onClick={() => navigate('/dashboard')} 
          style={{
            background: 'none',
            border: '1px solid #FFB6C1',
            color: '#FFB6C1',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '1.5rem'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>

        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: isMobile ? '1.6rem' : '2.5rem', marginBottom: '0.5rem', color: '#FFB6C1' }}>
            <FontAwesomeIcon icon={faUserFriends} /> Facilitator Dashboard
          </h1>
          <p style={{ color: '#999', fontSize: isMobile ? '0.95rem' : '1.1rem' }}>
            Manage your buddy matches and incoming requests
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmall ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: isMobile ? '0.75rem' : '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#999', fontSize: '0.9rem', margin: 0 }}>
                  PENDING REQUESTS
                </h3>
                <FontAwesomeIcon icon={faClock} style={{ color: '#fbbf24', fontSize: '1.5rem' }} />
              </div>
              <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#fbbf24', margin: 0 }}>
                {stats.pending_requests}
              </p>
            </div>

            <div style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#999', fontSize: '0.9rem', margin: 0 }}>
                  ACTIVE MATCHES
                </h3>
                <FontAwesomeIcon icon={faUsers} style={{ color: '#10b981', fontSize: '1.5rem' }} />
              </div>
              <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#10b981', margin: 0 }}>
                {stats.active_matches}
              </p>
              <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                of {stats.max_capacity} capacity
              </p>
            </div>

            <div style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#999', fontSize: '0.9rem', margin: 0 }}>
                  AVAILABILITY
                </h3>
                <FontAwesomeIcon icon={faChartBar} style={{ color: '#FFB6C1', fontSize: '1.5rem' }} />
              </div>
              <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#FFB6C1', margin: 0 }}>
                {stats.availability}
              </p>
              <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                slots available
              </p>
            </div>

            <div style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#999', fontSize: '0.9rem', margin: 0 }}>
                  TOTAL MATCHES
                </h3>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#8b5cf6', fontSize: '1.5rem' }} />
              </div>
              <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#8b5cf6', margin: 0 }}>
                {stats.total_matches}
              </p>
              <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                all time
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: isMobile ? '0' : '1rem',
          marginBottom: '1.5rem',
          borderBottom: '1px solid #333',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          <button
            onClick={() => setActiveTab('pending')}
            style={{
              padding: isMobile ? '0.75rem 1rem' : '1rem 2rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'pending' ? '2px solid #FFB6C1' : '2px solid transparent',
              color: activeTab === 'pending' ? '#FFB6C1' : '#999',
              fontSize: isMobile ? '0.9rem' : '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            Pending ({pendingRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('active')}
            style={{
              padding: isMobile ? '0.75rem 1rem' : '1rem 2rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'active' ? '2px solid #FFB6C1' : '2px solid transparent',
              color: activeTab === 'active' ? '#FFB6C1' : '#999',
              fontSize: isMobile ? '0.9rem' : '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            Active Matches ({activeMatches.length})
          </button>
        </div>

        {/* Pending Requests Tab */}
        {activeTab === 'pending' && (
          <div>
            {pendingRequests.length === 0 ? (
              <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '12px',
                padding: '3rem',
                textAlign: 'center'
              }}>
                <FontAwesomeIcon 
                  icon={faEnvelope} 
                  style={{ fontSize: '4rem', color: '#333', marginBottom: '1rem' }} 
                />
                <h3 style={{ color: '#999', marginBottom: '0.5rem' }}>
                  No Pending Requests
                </h3>
                <p style={{ color: '#666' }}>
                  You're all caught up! New match requests will appear here.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {pendingRequests.map(request => (
                  <div
                    key={request.id}
                    style={{
                      backgroundColor: '#1a1a1a',
                      border: selectedRequest?.id === request.id ? '2px solid #FFB6C1' : '1px solid #333',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      transition: 'all 0.3s'
                    }}
                  >
                    {/* Requester Info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      marginBottom: '1.5rem'
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        backgroundColor: '#FFB6C1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.75rem',
                        fontWeight: '700',
                        color: '#000'
                      }}>
                        {request.requester?.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ 
                          color: '#FFB6C1', 
                          marginBottom: '0.25rem',
                          fontSize: '1.3rem'
                        }}>
                          {request.requester?.full_name || 'Unknown'}
                        </h3>
                        <p style={{ 
                          color: '#999', 
                          margin: 0,
                          fontSize: '0.9rem'
                        }}>
                          Requested {formatDate(request.requested_at)}
                        </p>
                      </div>
                    </div>

                    {/* Request Message */}
                    <div style={{
                      backgroundColor: '#000',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      padding: '1.25rem',
                      marginBottom: '1rem'
                    }}>
                      <h4 style={{ 
                        color: '#FFB6C1', 
                        marginBottom: '0.75rem',
                        fontSize: '1rem'
                      }}>
                        Request Message:
                      </h4>
                      <p style={{ 
                        color: '#ccc', 
                        margin: 0,
                        lineHeight: '1.6',
                        fontSize: '1rem'
                      }}>
                        "{request.request_message}"
                      </p>
                    </div>

                    {/* Concern Areas */}
                    {request.concern_areas && request.concern_areas.length > 0 && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ 
                          color: '#999', 
                          marginBottom: '0.75rem',
                          fontSize: '0.9rem'
                        }}>
                          Areas of Concern:
                        </h4>
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '0.5rem' 
                        }}>
                          {request.concern_areas.map((area, index) => (
                            <span
                              key={index}
                              style={{
                                padding: '0.375rem 0.875rem',
                                backgroundColor: '#000',
                                border: '1px solid #FFB6C1',
                                borderRadius: '20px',
                                fontSize: '0.85rem',
                                color: '#FFB6C1'
                              }}
                            >
                              {area.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Response Form (if selected) */}
                    {selectedRequest?.id === request.id && (
                      <div style={{
                        backgroundColor: '#000',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        padding: '1.25rem',
                        marginBottom: '1rem'
                      }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          color: '#FFB6C1',
                          fontSize: '0.95rem'
                        }}>
                          Your Response (Optional):
                        </label>
                        <textarea
                          value={responseMessage}
                          onChange={(e) => setResponseMessage(e.target.value)}
                          placeholder="Add a message to your response..."
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '1rem',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '0.75rem', flexDirection: isMobile ? 'column' : 'row' }}>
                      {selectedRequest?.id === request.id ? (
                        <>
                          <button
                            onClick={() => {
                              setSelectedRequest(null);
                              setResponseMessage('');
                            }}
                            style={{
                              flex: 1,
                              padding: '0.875rem',
                              backgroundColor: '#1a1a1a',
                              border: '1px solid #333',
                              borderRadius: '8px',
                              color: '#ccc',
                              fontSize: '1rem',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleRejectRequest(request.id, responseMessage)}
                            disabled={actionLoading}
                            style={{
                              flex: 1,
                              padding: '0.875rem',
                              backgroundColor: '#ef4444',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#fff',
                              fontSize: '1rem',
                              fontWeight: '600',
                              cursor: actionLoading ? 'not-allowed' : 'pointer',
                              opacity: actionLoading ? 0.7 : 1
                            }}
                          >
                            <FontAwesomeIcon icon={faTimesCircle} /> Reject
                          </button>
                          <button
                            onClick={() => handleAcceptRequest(request.id, responseMessage)}
                            disabled={actionLoading}
                            style={{
                              flex: 1,
                              padding: '0.875rem',
                              backgroundColor: '#10b981',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#fff',
                              fontSize: '1rem',
                              fontWeight: '600',
                              cursor: actionLoading ? 'not-allowed' : 'pointer',
                              opacity: actionLoading ? 0.7 : 1
                            }}
                          >
                            <FontAwesomeIcon icon={faCheckCircle} /> Accept
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setSelectedRequest(request)}
                            style={{
                              flex: 1,
                              padding: '0.875rem',
                              backgroundColor: '#FFB6C1',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#000',
                              fontSize: '1rem',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Respond
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Active Matches Tab */}
        {activeTab === 'active' && (
          <div>
            {activeMatches.length === 0 ? (
              <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '12px',
                padding: '3rem',
                textAlign: 'center'
              }}>
                <FontAwesomeIcon 
                  icon={faUsers} 
                  style={{ fontSize: '4rem', color: '#333', marginBottom: '1rem' }} 
                />
                <h3 style={{ color: '#999', marginBottom: '0.5rem' }}>
                  No Active Matches
                </h3>
                <p style={{ color: '#666' }}>
                  Accept match requests to start helping people!
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '1rem'
              }}>
                {activeMatches.map(match => (
                  <div
                    key={match.id}
                    style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                      borderRadius: '12px',
                      padding: '1.5rem'
                    }}
                  >
                    {/* User Info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        backgroundColor: '#FFB6C1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        color: '#000'
                      }}>
                        {match.user?.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <h3 style={{ 
                          color: '#FFB6C1', 
                          margin: 0,
                          marginBottom: '0.25rem',
                          fontSize: '1.2rem'
                        }}>
                          {match.user?.full_name || 'Unknown'}
                        </h3>
                        <p style={{ 
                          color: '#999', 
                          margin: 0,
                          fontSize: '0.85rem'
                        }}>
                          Matched {formatDate(match.matched_at)}
                        </p>
                      </div>
                    </div>

                    {/* Last Interaction */}
                    {match.last_interaction && (
                      <div style={{
                        backgroundColor: '#000',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        padding: '0.875rem',
                        marginBottom: '1rem'
                      }}>
                        <p style={{ 
                          color: '#999', 
                          margin: 0,
                          fontSize: '0.85rem'
                        }}>
                          Last interaction: {formatDate(match.last_interaction)}
                        </p>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => navigate(`/buddy/chat/${match.id}`)}
                        style={{
                          flex: 1,
                          padding: '0.625rem',
                          backgroundColor: '#3b82f6',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.9rem',
                          cursor: 'pointer'
                        }}
                        title="Send message"
                      >
                        <FontAwesomeIcon icon={faMessage} />
                      </button>
                      <button
                        style={{
                          flex: 1,
                          padding: '0.625rem',
                          backgroundColor: '#10b981',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.9rem',
                          cursor: 'pointer'
                        }}
                        title="Call"
                      >
                        <FontAwesomeIcon icon={faPhone} />
                      </button>
                      <button
                        style={{
                          flex: 1,
                          padding: '0.625rem',
                          backgroundColor: '#8b5cf6',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.9rem',
                          cursor: 'pointer'
                        }}
                        title="Video call"
                      >
                        <FontAwesomeIcon icon={faVideo} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilitatorDashboard;
